import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ScrollArea } from "../ui/scroll-area";
import {
  FileText,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Search,
  Eye,
  Download,
} from "lucide-react";
import { leadUploadService } from "../../services/leadUploadService";
import { LeadUpload, LeadUploadStatus } from "../../types/leadUpload";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ManualReviewForm } from "./ManualReviewForm";
import { useToast } from "../ui/use-toast";
import { toast } from "../ui/use-toast";

interface LeadUploadsListProps {
  organizationId: string;
  campaignId?: string;
  campaignTemplateId?: string;
  onRefresh?: () => void;
}

export function LeadUploadsList({
  organizationId,
  campaignId,
  campaignTemplateId,
  onRefresh,
}: LeadUploadsListProps) {
  const [leadUploads, setLeadUploads] = useState<LeadUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [activeTab, setActiveTab] = useState<"all" | "needs_review">("all");

  const fetchLeadUploads = async () => {
    setLoading(true);
    try {
      let result;

      // If campaignId is provided, fetch uploads for that campaign
      if (campaignId) {
        result = await leadUploadService.getLeadUploadsByCampaign(campaignId);
      }
      // If campaignTemplateId is provided, fetch uploads for that campaign template
      else if (campaignTemplateId) {
        result =
          await leadUploadService.getLeadUploadsByCampaignTemplate(
            campaignTemplateId,
          );
      } else if (activeTab === "needs_review") {
        result = await leadUploadService.getLeadUploadsByStatus(
          organizationId,
          "needs_review",
        );
      } else {
        result = await leadUploadService.getLeadUploads(organizationId);
      }

      if (result.data) {
        setLeadUploads(result.data);
      }
    } catch (error) {
      console.error("Error fetching lead uploads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadUploads();
  }, [organizationId, campaignId, campaignTemplateId, activeTab]);

  const handleRefresh = () => {
    fetchLeadUploads();
    if (onRefresh) {
      onRefresh();
    }
  };

  const getStatusBadge = (status: LeadUploadStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="outline">
            <Clock className="h-3 w-3 mr-1" /> Pending
          </Badge>
        );
      case "processing":
        return (
          <Badge variant="secondary">
            <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Processing
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="good">
            <CheckCircle className="h-3 w-3 mr-1" /> Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" /> Failed
          </Badge>
        );
      case "needs_review":
        return (
          <Badge variant="mid">
            <Search className="h-3 w-3 mr-1" /> Needs Review
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleManualReviewComplete = () => {
    fetchLeadUploads();
  };

  const handleViewContent = async (uploadId: string) => {
    setViewingContent({ id: uploadId, loading: true });
    try {
      const { data, error } = await leadUploadService.getMdContent(uploadId);
      if (error) throw error;

      console.log("Got content from service:", data);

      setViewingContent({
        id: uploadId,
        loading: false,
        mdContent: data?.mdContent, // Note the case change to match service response
        extractedData: data?.extractedData, // Note the case change to match service response
      });
    } catch (error) {
      console.error("Error fetching content:", error);
      toast({
        title: "Error",
        description: "Failed to load file content",
        variant: "destructive",
      });
      setViewingContent(null);
    }
  };

  const handleDownloadFile = async (upload: LeadUpload) => {
    setDownloadingId(upload.id);
    try {
      // The service expects the file path, not the upload ID
      const { url, error } = await leadUploadService.getFileDownloadUrl(
        upload.file_path,
      );
      if (error) throw error;

      if (url) {
        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = url;
        link.download = upload.original_filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error("Could not generate download URL");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRetry = async (uploadId: string) => {
    setRetryingId(uploadId);
    try {
      toast({
        title: "Processing",
        description: "Attempting to extract data again...",
      });

      const { data, error } =
        await leadUploadService.processLeadUpload(uploadId);

      if (error) {
        throw error;
      }

      toast({
        title: "Processing complete",
        description: data
          ? "Data extraction successful"
          : "Processing completed, please check the status",
      });

      fetchLeadUploads();
    } catch (error) {
      console.error("Error retrying lead processing:", error);
      toast({
        title: "Error",
        description: "Failed to process the upload. Please try manual review.",
        variant: "destructive",
      });
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Card className="rounded-none border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Lead Uploads</CardTitle>
          <CardDescription>Manage your uploaded lead files</CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="rounded-none"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs
          defaultValue="all"
          onValueChange={(value) =>
            setActiveTab(value as "all" | "needs_review")
          }
        >
          <TabsList className="mb-4">
            <TabsTrigger value="all">All Uploads</TabsTrigger>
            <TabsTrigger value="needs_review">Needs Review</TabsTrigger>
          </TabsList>
          <TabsContent value="all">{renderTable()}</TabsContent>
          <TabsContent value="needs_review">{renderTable()}</TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );

  function renderTable() {
    if (loading) {
      return <div className="py-8 text-center">Loading...</div>;
    }

    if (leadUploads.length === 0) {
      return (
        <div className="py-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-ink-faint" />
          <p className="mt-2 text-muted-foreground">
            {activeTab === "needs_review"
              ? "No uploads need review"
              : "No lead uploads found"}
          </p>
        </div>
      );
    }

    return (
      <>
        <style>{`
          .table-scroll-wrapper > div {
            overflow: visible !important;
          }
        `}</style>
        <div className="border rounded-md h-[500px] overflow-hidden">
          <div className="h-full overflow-y-auto table-scroll-wrapper">
            <Table className="border-0">
              <TableHeader className="sticky top-0 z-10 bg-background border-b">
                <TableRow className="border-b">
                  <TableHead className="bg-background">Filename</TableHead>
                  <TableHead className="bg-background">Status</TableHead>
                  <TableHead className="bg-background">Uploaded</TableHead>
                  <TableHead className="text-right bg-background">Actions</TableHead>
                </TableRow>
              </TableHeader>
          <TableBody>
            {leadUploads.map((upload) => (
              <TableRow key={upload.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-ink-faint" />
                    <span className="truncate max-w-[200px]">
                      {upload.original_filename}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(upload.upload_status as LeadUploadStatus)}
                </TableCell>
                <TableCell>
                  {formatDistanceToNow(new Date(upload.created_at), {
                    addSuffix: true,
                  })}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    {/* View Content Button - Hide for CSV imports */}
                    {!upload.file_path.includes("csv_imports/") && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewContent(upload.id);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>File Content</DialogTitle>
                        </DialogHeader>
                        {viewingContent?.id === upload.id ? (
                          viewingContent.loading ? (
                            <div className="py-8 text-center">
                              <RefreshCw className="h-8 w-8 mx-auto text-ink-faint animate-spin" />
                              <p className="mt-2">Loading content...</p>
                            </div>
                          ) : (
                            <Tabs defaultValue="extracted">
                              <TabsList className="mb-4">
                                <TabsTrigger value="extracted">
                                  Extracted Data
                                </TabsTrigger>
                                <TabsTrigger value="raw">
                                  Raw Content
                                </TabsTrigger>
                              </TabsList>
                              <TabsContent value="extracted">
                                <ScrollArea className="h-[60vh]">
                                  <div className="p-4 border rounded-md bg-muted/50">
                                    <h3 className="text-lg font-medium mb-2">
                                      Extracted Information
                                    </h3>
                                    {viewingContent.extractedData ? (
                                      <pre className="whitespace-pre-wrap overflow-auto p-4 bg-card rounded-md">
                                        {JSON.stringify(
                                          viewingContent.extractedData,
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    ) : (
                                      <p className="text-muted-foreground">
                                        No extracted data available
                                      </p>
                                    )}
                                  </div>
                                </ScrollArea>
                              </TabsContent>
                              <TabsContent value="raw">
                                <ScrollArea className="h-[60vh]">
                                  <div className="p-4 border rounded-md bg-muted/50">
                                    <h3 className="text-lg font-medium mb-2">
                                      Raw Content
                                    </h3>
                                    {viewingContent.mdContent ? (
                                      <pre className="whitespace-pre-wrap overflow-auto p-4 bg-card rounded-md">
                                        {typeof viewingContent.mdContent ===
                                        "string"
                                          ? viewingContent.mdContent
                                          : JSON.stringify(
                                              viewingContent.mdContent,
                                              null,
                                              2,
                                            )}
                                      </pre>
                                    ) : (
                                      <p className="text-muted-foreground">
                                        No content available
                                      </p>
                                    )}
                                  </div>
                                </ScrollArea>
                              </TabsContent>
                            </Tabs>
                          )
                        ) : (
                          <div className="py-8 text-center">
                            <p>No content selected</p>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    )}

                    {/* Download Button - Hide for CSV imports */}
                    {!upload.file_path.includes("csv_imports/") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadFile(upload);
                        }}
                        disabled={downloadingId === upload.id}
                      >
                        {downloadingId === upload.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </>
                        )}
                      </Button>
                    )}

                    {/* Needs Review Actions */}
                    {upload.upload_status === "needs_review" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetry(upload.id);
                          }}
                          disabled={retryingId === upload.id}
                        >
                          {retryingId === upload.id ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Retrying...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Retry
                            </>
                          )}
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Review
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Review Lead Data</DialogTitle>
                            </DialogHeader>
                            <ManualReviewForm
                              leadUpload={upload}
                              onComplete={handleManualReviewComplete}
                            />
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            </Table>
          </div>
        </div>
      </>
    );
  }
}
